import { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GoogleMap from '../components/GoogleMap';

const Kontakt = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    standort: 'leipzig',
    betreff: '',
    nachricht: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          telefon: '',
          standort: 'leipzig',
          betreff: '',
          nachricht: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-turquoise-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-16 px-4 overflow-hidden" data-testid="kontakt-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-turquoise-400 to-turquoise-300 opacity-10"></div>
        <div className="container mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Kontaktieren Sie uns
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Wir sind für Sie da – kontaktieren Sie uns für eine unverbindliche Beratung
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8" data-testid="contact-form-container">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Nachricht senden
              </h2>
              
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6" data-testid="success-message">
                  <p className="font-semibold">Vielen Dank für Ihre Nachricht!</p>
                  <p className="text-sm">Wir werden uns so schnell wie möglich bei Ihnen melden.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6" data-testid="error-message">
                  <p className="font-semibold">Es ist ein Fehler aufgetreten.</p>
                  <p className="text-sm">Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent transition-all"
                      placeholder="Ihr Name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent transition-all"
                      placeholder="ihre@email.de"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent transition-all"
                      placeholder="0123 456789"
                      data-testid="input-telefon"
                    />
                  </div>
                  <div>
                    <label htmlFor="standort" className="block text-sm font-medium text-gray-700 mb-1">
                      Standort
                    </label>
                    <select
                      id="standort"
                      name="standort"
                      value={formData.standort}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent transition-all bg-white"
                      data-testid="select-standort"
                    >
                      <option value="leipzig">Leipzig</option>
                      <option value="halle">Halle (Saale)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="betreff" className="block text-sm font-medium text-gray-700 mb-1">
                    Betreff *
                  </label>
                  <input
                    type="text"
                    id="betreff"
                    name="betreff"
                    value={formData.betreff}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent transition-all"
                    placeholder="Worum geht es?"
                    data-testid="input-betreff"
                  />
                </div>

                <div>
                  <label htmlFor="nachricht" className="block text-sm font-medium text-gray-700 mb-1">
                    Nachricht *
                  </label>
                  <textarea
                    id="nachricht"
                    name="nachricht"
                    value={formData.nachricht}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent transition-all resize-none"
                    placeholder="Ihre Nachricht an uns..."
                    data-testid="input-nachricht"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-turquoise-400 text-white py-4 px-6 rounded-xl font-semibold hover:bg-turquoise-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Wird gesendet...
                    </>
                  ) : (
                    'Nachricht senden'
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              {/* Leipzig */}
              <div className="bg-white rounded-3xl shadow-xl p-8" data-testid="contact-leipzig">
                <div className="flex items-center mb-4">
                  <span className="bg-turquoise-400 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 font-bold">L</span>
                  <h3 className="text-xl font-bold text-gray-900">Leipzig</h3>
                </div>
                <div className="space-y-4 ml-14">
                  <div>
                    <p className="text-gray-600">
                      Ambulante Pflege Großstadtherzen® UG<br />
                      Zschochersche Str. 49<br />
                      04229 Leipzig
                    </p>
                  </div>
                  <div className="space-y-2">
                    <a href="tel:034124927833" className="flex items-center text-turquoise-400 hover:text-turquoise-500 transition-colors font-medium">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      0341-24927833
                    </a>
                    <a href="mailto:info@grossstadtherzen.de" className="flex items-center text-turquoise-400 hover:text-turquoise-500 transition-colors">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      info@grossstadtherzen.de
                    </a>
                  </div>
                  <p className="text-sm text-gray-500">
                    Mo - Fr: 8:00 - 16:00 Uhr
                  </p>
                </div>
              </div>

              {/* Halle */}
              <div className="bg-white rounded-3xl shadow-xl p-8" data-testid="contact-halle">
                <div className="flex items-center mb-4">
                  <span className="bg-turquoise-400 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 font-bold">H</span>
                  <h3 className="text-xl font-bold text-gray-900">Halle (Saale)</h3>
                </div>
                <div className="space-y-4 ml-14">
                  <div>
                    <p className="text-gray-600">
                      Ambulante Pflege Großstadtherzen® Halle UG<br />
                      Merseburger Str. 19<br />
                      06112 Halle (Saale)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <a href="tel:034568669465" className="flex items-center text-turquoise-400 hover:text-turquoise-500 transition-colors font-medium">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      0345-68669465
                    </a>
                    <a href="mailto:info@grossstadtherzen-halle.de" className="flex items-center text-turquoise-400 hover:text-turquoise-500 transition-colors">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      info@grossstadtherzen-halle.de
                    </a>
                  </div>
                  <p className="text-sm text-gray-500">
                    Mo - Fr: 8:00 - 16:00 Uhr
                  </p>
                </div>
              </div>

              {/* Quick CTA */}
              <div className="bg-gradient-to-br from-turquoise-400 to-turquoise-500 rounded-3xl shadow-xl p-8 text-white">
                <h3 className="text-xl font-bold mb-3">Schnelle Hilfe benötigt?</h3>
                <p className="mb-4 text-turquoise-100">
                  Rufen Sie uns direkt an – wir helfen Ihnen gerne weiter.
                </p>
                <a 
                  href="tel:034124927833"
                  className="inline-flex items-center bg-white text-turquoise-500 px-6 py-3 rounded-full font-semibold hover:bg-turquoise-50 transition-all"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Jetzt anrufen
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maps Section */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Unsere Standorte</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-turquoise-400 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">L</span>
                Leipzig
              </h3>
              <GoogleMap location="leipzig" />
              <p className="text-sm text-gray-500 mt-2">Zschochersche Str. 49, 04229 Leipzig</p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-turquoise-400 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">H</span>
                Halle (Saale)
              </h3>
              <GoogleMap location="halle" />
              <p className="text-sm text-gray-500 mt-2">Merseburger Str. 19, 06112 Halle (Saale)</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Kontakt;
